import { ConfigurationResponseSchema, EventSchema } from '@ais-ui/api-spec/s2s';
import { useCallback, useMemo } from 'react';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import {
  AccordionContent,
  AccordionListItem,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncatedText,
  useToast,
} from '@ais-ui/ui-core';
import { capitalize, cn, isNonEmpty } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useShipSelection } from '../../../Map/contexts';
import {
  useLayers,
  useOpenLayersContext,
  smartZoomTo,
  shapeToVectorLayer,
  trackPathToVectorLayer,
  initLayerItem,
  baseColours,
  getCombinedExtent,
} from '@ais-ui/react-openlayers';
import {
  PiArrowsLeftRight,
  PiCheckCircle,
  PiDotsThreeVertical,
  PiWarningCircle,
} from 'react-icons/pi';
import { AutoCompleteShipInfo } from '@ais-ui/websockets';
import { transform } from 'ol/proj';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import {
  useAcknowledgeS2SEvent,
  useFetchAllS2SConfigurations,
  useGetCurrentS2SEvents,
  useFetchVectorTrack,
  useShapeFetch,
} from '@ais-ui/api';
import { usePermissions } from '../../../Permissions/PermissionsProvider';
import { EventGridItem } from '../../EventGridItem';
import OlGeometry from 'ol/geom/Geometry';
import { useTimeSince } from '../../../conversions/useTimeSince';
import { useEventListExtraFields } from '../../useEventListExtraFields';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Extent, createEmpty, extend } from 'ol/extent';
import { DateRange } from 'react-day-picker';
import { normalizeToFeature } from '../normalizeGeometry';
import { fromS2SAckState } from '../../ackState';

// --- S2S Track Helpers ---

const S2S_TRACK_BUFFER_MS = 20 * 60 * 1000;
const S2S_TRACK_COLORS = ['#e63946', '#457b9d'] as const;

/** Compute the time range for fetching tracks around an S2S event. */
function getS2SEventTimeRange(event: EventSchema) {
  const activationTime = event.activationTime
    ? new Date(event.activationTime)
    : new Date();
  const startTime = new Date(activationTime.getTime() - S2S_TRACK_BUFFER_MS);
  const endTime = event.deactivationTime
    ? new Date(new Date(event.deactivationTime).getTime() + S2S_TRACK_BUFFER_MS)
    : new Date();
  return { activationTime, startTime, endTime };
}

/** Build the vessel list for the two vessels in an S2S event. */
function getS2SVesselPair(event: EventSchema) {
  return [
    {
      mmsi: event.mmsiFirst,
      color: S2S_TRACK_COLORS[0],
    },
    {
      mmsi: event.mmsiSecond,
      color: S2S_TRACK_COLORS[1],
    },
  ];
}

// --- Map Point Hover Metadata ---

const ACTIVATION_POINT_DESCRIPTION =
  'Vessel position at the start of the detection window (activation time).';
const FIRED_POINT_DESCRIPTION =
  'Vessel position when the event was triggered (fired time).';

interface PointHoverInfo {
  title: string;
  subtitle?: string;
  description: string;
  timestamp?: Date | string | null;
}

/**
 * Build a styled map point feature carrying hover-tooltip metadata (read by
 * usePointHoverTooltip) so users can see what each S2S point means and when
 * it occurred.
 */
function createS2SPointFeature(
  coordinates: number[],
  dataProjection: string,
  displayProjection: string,
  style: Style,
  hover: PointHoverInfo,
): Feature {
  const transformed = transform(coordinates, dataProjection, displayProjection);
  const feature = new Feature({ geometry: new Point(transformed) });
  feature.setStyle(style);
  feature.set('hoverTitle', hover.title);
  if (hover.subtitle) feature.set('hoverSubtitle', hover.subtitle);
  feature.set('hoverDescription', hover.description);
  if (hover.timestamp) {
    feature.set(
      'hoverTimestamp',
      hover.timestamp instanceof Date
        ? hover.timestamp.toISOString()
        : hover.timestamp,
    );
  }
  return feature;
}

// --- Types ---

export interface CombinedS2SEventAndConfiguration {
  event: EventSchema;
  configuration?: ConfigurationResponseSchema;
}

// --- S2S Field Value Resolver ---

const getS2SEventFieldValue = (
  event: EventSchema,
  configuration: ConfigurationResponseSchema | undefined,
  fieldKey: string,
  formatDateTimeWithTimeZone: (
    date: Date | null | undefined,
    hideSeconds?: boolean,
  ) => string,
): string => {
  switch (fieldKey) {
    case 'status':
      return event.deactivationTime ? 'Deactivated' : 'Activated';
    case 'severity': {
      const sev = (event.severity ?? configuration?.severity ?? '')
        .toString()
        .toLowerCase();
      return sev ? sev.charAt(0).toUpperCase() + sev.slice(1) : '-';
    }
    case 'activatedTime':
      return event.activationTime
        ? formatDateTimeWithTimeZone(event.activationTime)
        : '-';
    case 'deactivatedTime':
      return event.deactivationTime
        ? formatDateTimeWithTimeZone(event.deactivationTime)
        : 'Still active';
    case 'mmsi':
      return (
        [event.mmsiFirst, event.mmsiSecond].filter(Boolean).join(' / ') || '-'
      );
    case 'callSign':
      return '-';
    case 'acknowledged':
      return fromS2SAckState(
        (event as unknown as Record<string, unknown>).ack_state as string,
      ) === 'acknowledged'
        ? 'Yes'
        : 'No';
    default:
      return '-';
  }
};

// --- Data Hook ---

export const useGetCurrentS2SEventsAndConfigurations = (
  queryDate: DateRange | undefined,
) => {
  const permissions = usePermissions();

  const shouldFetch = Boolean(
    permissions.s2s &&
    queryDate?.from !== undefined &&
    queryDate?.to !== undefined,
  );

  const { data: eventsData, isLoading: eventsLoading } = useGetCurrentS2SEvents(
    { from: queryDate?.from, to: queryDate?.to },
    shouldFetch,
  );

  const { data: configurationsData, isLoading: configsLoading } =
    useFetchAllS2SConfigurations(shouldFetch);

  const actualEventsLoading = shouldFetch ? eventsLoading : false;
  const actualConfigsLoading = shouldFetch ? configsLoading : false;

  const configurationMap = useMemo(() => {
    const items = configurationsData?.items || [];
    return new Map(
      items
        .filter((config) => config.id != null)
        .map((config) => [config.id, config]),
    );
  }, [configurationsData?.items]);

  const combinedEvents: CombinedS2SEventAndConfiguration[] = useMemo(() => {
    if (!eventsData || !configurationsData) return [];

    return (eventsData.items || [])
      .filter((event) => event.configurationId != null)
      .map((event) => {
        const configuration = configurationMap.get(event.configurationId!);
        return { event, configuration: configuration ?? undefined };
      });
  }, [eventsData, configurationsData, configurationMap]);

  return {
    s2sEventsAndConfigurations: combinedEvents,
    s2sIsLoading: actualEventsLoading || actualConfigsLoading,
  };
};

// --- Event Details ---

const S2SEventDetails = ({
  event,
  configuration,
}: CombinedS2SEventAndConfiguration) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const severity = (event.severity ?? configuration?.severity ?? '')
    .toString()
    .toLowerCase();

  return (
    <div className="flex flex-col space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-sm font-medium flex-none">
            {messages.eventsItemSeverityLabel}
          </p>
          <div className="flex items-center space-x-1.5">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                severity === 'note' && 'bg-blue-500',
                severity === 'warning' && 'bg-yellow-500',
                severity === 'alert' && 'bg-red-500',
              )}
            />
            <p className="text-sm text-muted-foreground flex-grow truncate">
              {isNonEmpty(capitalize(severity))
                ? capitalize(severity)
                : 'Unknown'}
            </p>
          </div>
        </div>
        <EventGridItem
          label={messages.eventsItemDetectorLabel}
          value={configuration?.name ?? 'Deleted detector'}
        />
      </div>
      <EventGridItem
        label={messages.eventsItemActivatedLabel}
        value={formatDateTimeWithTimeZone(event.activationTime)}
      />
      <EventGridItem
        label={messages.eventsItemDeactivatedLabel}
        value={
          formatDateTimeWithTimeZone(event.deactivationTime)
            ? formatDateTimeWithTimeZone(event.deactivationTime)
            : messages.eventsItemDeactivatedFalseValue
        }
      />
      {event.activationText && (
        <EventGridItem
          label={messages.eventsItemTextLabel}
          value={event.activationText}
          wrap
        />
      )}
    </div>
  );
};

// --- Dropdown Menu ---

const S2SEventDropDownMenu = ({
  event,
  configuration,
  className,
}: {
  event: EventSchema;
  configuration?: ConfigurationResponseSchema;
  className?: string;
}) => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { setZoomToShip } = useShipSelection();
  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const {
    addLayer,
    doesLayerExist,
    zoomToLayer,
    addLayerWithChildren,
    zoomToExtent,
  } = useLayers();
  const shapeFetch = useShapeFetch();
  const fetchVectorTrack = useFetchVectorTrack();
  const { mutate: acknowledgeS2S } = useAcknowledgeS2SEvent();

  const ackState = fromS2SAckState(
    (event as unknown as Record<string, unknown>).ack_state as string,
  );

  const detectorName = configuration?.name ?? 'Unknown';
  const detectorShapeNameId = `S2S Detector ${detectorName}`;

  const onGotoEvent = useCallback(() => {
    const firstPos = event.s2sPair.first.activationPosition;
    const secondPos = event.s2sPair.second.activationPosition;

    if (!firstPos && !secondPos) {
      toast({
        title: 'No position data',
        description: 'This event does not have activation positions available.',
        variant: 'destructive',
      });
      return;
    }

    const features: Feature[] = [];
    const vesselStyle = new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: 'orange' }),
        stroke: new Stroke({ color: 'black', width: 1 }),
      }),
    });

    const extent: Extent = createEmpty();

    for (const pos of [firstPos, secondPos]) {
      if (!pos?.coordinates) continue;
      const transformed = transform(
        pos.coordinates,
        dataProjection,
        displayProjection,
      );
      const feature = new Feature({ geometry: new Point(transformed) });
      feature.setStyle(vesselStyle);
      features.push(feature);
      extend(extent, [
        transformed[0],
        transformed[1],
        transformed[0],
        transformed[1],
      ]);
    }

    if (features.length > 0) {
      // Zoom to the midpoint or extent of both vessels
      if (features.length === 1) {
        const geom = features[0].getGeometry() as Point;
        smartZoomTo({ coordinate: geom.getCoordinates(), map });
      } else {
        map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 14 });
      }

      const vectorLayer = new VectorLayer({
        source: new VectorSource({ features }),
      });

      const layerId =
        detectorName +
        (event.mmsiFirst?.toString() || '') +
        (event.mmsiSecond?.toString() || '');

      addLayer(vectorLayer, layerId, true, true, true, {}, '', false, true);
    }
  }, [
    event,
    detectorName,
    dataProjection,
    displayProjection,
    map,
    addLayer,
    toast,
  ]);

  const onShowShape = useCallback(
    async (shapeId: string | null | undefined) => {
      if (!configuration) {
        toast({
          title: 'Detector not found',
          description:
            'The detector for this event has been deleted and can no longer be displayed.',
          variant: 'destructive',
        });
        return;
      }

      if (doesLayerExist(detectorShapeNameId)) return;

      let scopedVectorLayer: VectorLayer<Feature<OlGeometry>> | undefined;
      let shape: GeoJson | undefined;

      if (shapeId) {
        shape = await shapeFetch(shapeId);
        if (!shape) {
          toast({
            title: 'Shape not found',
            description: 'Could not load the detector shape.',
            variant: 'destructive',
          });
          return;
        }
        scopedVectorLayer = shapeToVectorLayer(
          shape,
          displayProjection,
          dataProjection,
        );
      } else if (configuration.geometry) {
        shape = normalizeToFeature(
          configuration.geometry as unknown as GeoJson,
        );
        scopedVectorLayer = shapeToVectorLayer(
          shape,
          displayProjection,
          dataProjection,
        );
      } else {
        toast({
          title: 'No detector shape',
          description:
            'This detector does not have a shape or geometry configured.',
          variant: 'destructive',
        });
        return;
      }

      addLayer(
        scopedVectorLayer,
        detectorShapeNameId,
        true,
        true,
        true,
        (shape as unknown as Record<string, unknown>)?.properties ?? {},
        detectorShapeNameId,
        true,
      );
      zoomToLayer(scopedVectorLayer);
    },
    [
      configuration,
      doesLayerExist,
      detectorShapeNameId,
      shapeFetch,
      addLayer,
      zoomToLayer,
      displayProjection,
      dataProjection,
      toast,
    ],
  );

  const onSelectFirstVessel = useCallback(() => {
    const vessel = event.s2sPair.first;
    const newSelectedShip: AutoCompleteShipInfo = {
      callSign: null,
      imo: null,
      mmsi: vessel.mmsi ? vessel.mmsi.toString() : null,
      name: null,
      type: 'TT_SHIP',
    };
    setZoomToShip(newSelectedShip);
  }, [event.s2sPair.first, setZoomToShip]);

  const onSelectSecondVessel = useCallback(() => {
    const vessel = event.s2sPair.second;
    const newSelectedShip: AutoCompleteShipInfo = {
      callSign: null,
      imo: null,
      mmsi: vessel.mmsi ? vessel.mmsi.toString() : null,
      name: null,
      type: 'TT_SHIP',
    };
    setZoomToShip(newSelectedShip);
  }, [event.s2sPair.second, setZoomToShip]);

  const onAcknowledge = useCallback(() => {
    if (!event.id) return;
    acknowledgeS2S(event.id, {
      onSuccess: () => {
        toast({
          title: 'Success!',
          description: messages.eventsAcknowledgedEventSuccess,
        });
      },
      onError: () => {
        toast({
          title: 'Error!',
          description: messages.eventsAcknowledgedEventError,
          variant: 'destructive',
        });
      },
    });
  }, [event.id, acknowledgeS2S, toast, messages]);

  const onShowOnMap = useCallback(async () => {
    const groupId = `s2s-event-${event.uuid}`;
    if (doesLayerExist(groupId + '-points')) return;

    const mmsiFirst = event.mmsiFirst;
    const mmsiSecond = event.mmsiSecond;
    const groupName = 'S2S Event';
    const mmsiLabel = [mmsiFirst, mmsiSecond].filter(Boolean).join(' / ');

    // Build point features for activation positions
    const firstPos = event.s2sPair.first.activationPosition;
    const secondPos = event.s2sPair.second.activationPosition;
    const features: Feature[] = [];
    const vesselStyle = new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: 'orange' }),
        stroke: new Stroke({ color: 'black', width: 1 }),
      }),
    });

    const activationEntries = [
      { pos: firstPos, mmsi: event.mmsiFirst },
      { pos: secondPos, mmsi: event.mmsiSecond },
    ];
    for (const { pos, mmsi } of activationEntries) {
      if (!pos?.coordinates) continue;
      features.push(
        createS2SPointFeature(
          pos.coordinates,
          dataProjection,
          displayProjection,
          vesselStyle,
          {
            title: 'Activation position',
            subtitle: mmsi ? `MMSI ${mmsi}` : undefined,
            description: ACTIVATION_POINT_DESCRIPTION,
            timestamp: event.activationTime,
          },
        ),
      );
    }

    const childLayerItems = [];

    // Add detections/points layer for activation positions
    if (features.length > 0) {
      const pointsLayer = new VectorLayer({
        source: new VectorSource({ features }),
      });
      const pointsItem = initLayerItem(
        pointsLayer,
        'Activation Positions',
        false,
        true,
        true,
        false,
        { type: 'point', layerMetadata: `${features.length}` },
        groupId + '-points',
      );
      pointsItem.color = baseColours.orange;
      childLayerItems.push(pointsItem);
    }

    // Build point features for fired positions
    const firstFiredPos = event.s2sPair.first.firedPosition;
    const secondFiredPos = event.s2sPair.second.firedPosition;
    const firedFeatures: Feature[] = [];
    const firedStyle = new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: baseColours.red }),
        stroke: new Stroke({ color: 'black', width: 1 }),
      }),
    });

    const firedTime = (event as unknown as Record<string, unknown>)
      .fired_time as string | null;
    const firedEntries = [
      { pos: firstFiredPos, mmsi: event.mmsiFirst },
      { pos: secondFiredPos, mmsi: event.mmsiSecond },
    ];
    for (const { pos, mmsi } of firedEntries) {
      if (!pos?.coordinates) continue;
      firedFeatures.push(
        createS2SPointFeature(
          pos.coordinates,
          dataProjection,
          displayProjection,
          firedStyle,
          {
            title: 'Fired position',
            subtitle: mmsi ? `MMSI ${mmsi}` : undefined,
            description: FIRED_POINT_DESCRIPTION,
            timestamp: firedTime,
          },
        ),
      );
    }

    if (firedFeatures.length > 0) {
      const firedLayer = new VectorLayer({
        source: new VectorSource({ features: firedFeatures }),
      });
      const firedItem = initLayerItem(
        firedLayer,
        'Fired Positions',
        false,
        true,
        true,
        false,
        { type: 'point', layerMetadata: `${firedFeatures.length}` },
        groupId + '-fired-points',
      );
      firedItem.color = baseColours.red;
      childLayerItems.push(firedItem);
    }

    // Fetch and add tracks
    const { activationTime, startTime, endTime } = getS2SEventTimeRange(event);
    const vessels = getS2SVesselPair(event);

    for (const vessel of vessels) {
      if (!vessel.mmsi) continue;
      try {
        const result = await fetchVectorTrack({
          mmsi: vessel.mmsi,
          startTime,
          endTime,
        });
        const track = result.tracks[0];
        if (!track?.path) continue;

        // Annotate waypoints with event phase for potential styling
        const activationMs = activationTime.getTime();
        const deactivationMs = event.deactivationTime
          ? new Date(event.deactivationTime).getTime()
          : null;
        for (const wp of track.waypoints) {
          const wpMs = wp.timestamp ? new Date(wp.timestamp).getTime() : 0;
          const phase =
            wpMs < activationMs
              ? 'before'
              : deactivationMs && wpMs > deactivationMs
                ? 'after'
                : 'during';
          wp.annotations = { ...wp.annotations, event_phase: phase };
        }

        const displayName = String(vessel.mmsi);
        const vectorLayer = trackPathToVectorLayer(
          track,
          displayProjection,
          dataProjection,
          { color: vessel.color, label: displayName },
        );

        const trackItem = initLayerItem(
          vectorLayer,
          `Track ${displayName}`,
          false,
          true,
          true,
          false,
          {
            type: 'vector-track',
            track,
            defaultColor: vessel.color,
            layerMetadata: String(vessel.mmsi),
          },
          `${groupId}-track-${vessel.mmsi}`,
        );
        trackItem.color = vessel.color;
        childLayerItems.push(trackItem);
      } catch (err) {
        console.warn(
          `[S2S] Failed to fetch track for MMSI ${vessel.mmsi}:`,
          err,
        );
      }
    }

    if (childLayerItems.length > 0) {
      addLayerWithChildren(
        {
          id: groupId,
          name: groupName,
          metadata: mmsiLabel,
          isBase: false,
          isVisible: true,
          isRemovableFromLayerControl: true,
          canZoomToFromLayerControl: true,
          canShowLabel: false,
          properties: { type: 's2s-event' },
          type: 'featurecollection',
        },
        childLayerItems,
      );

      // Zoom to combined extent
      const combinedExtent = getCombinedExtent(childLayerItems);
      if (combinedExtent) {
        zoomToExtent(combinedExtent);
      }
    } else {
      toast({
        title: 'No data',
        description: 'Could not load event layers.',
        variant: 'destructive',
      });
    }
  }, [
    event,
    detectorName,
    doesLayerExist,
    fetchVectorTrack,
    addLayerWithChildren,
    zoomToExtent,
    displayProjection,
    dataProjection,
    toast,
  ]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button size="icon" variant="ghost" className={cn('size-7')}>
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShowOnMap}>Show on map</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSelectFirstVessel}>
          Select {event.mmsiFirst ? `MMSI ${event.mmsiFirst}` : 'Vessel 1'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSelectSecondVessel}>
          Select {event.mmsiSecond ? `MMSI ${event.mmsiSecond}` : 'Vessel 2'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onShowShape(configuration?.shapeUuid)}>
          {messages.eventsActionsShowDetector}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={ackState === 'acknowledged'}
          onClick={onAcknowledge}
        >
          {messages.eventsActionsAcknowledge}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// --- Accordion Content ---

const S2SEventItemContent = ({
  event,
  configuration,
}: CombinedS2SEventAndConfiguration) => {
  const { messages } = useLocalization();

  return (
    <AccordionContent>
      <div className="grid gap-6 px-4 pt-4 [&_*]:min-w-0">
        <div className="grid gap-4">
          <div>
            <h3 className="font-medium text-base">Vessels</h3>
            <Separator className="mt-2" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <EventGridItem
              label={`${messages.eventsItemMmsiLabel} Vessel 1`}
              value={event.s2sPair.first.mmsi?.toString()}
            />
            <EventGridItem
              label={`${messages.eventsItemMmsiLabel} Vessel 2`}
              value={event.s2sPair.second.mmsi?.toString()}
            />
          </div>
        </div>
        <div className="grid gap-4">
          <div>
            <h3 className="font-medium text-base">Event</h3>
            <Separator className="mt-2" />
          </div>
          <S2SEventDetails event={event} configuration={configuration} />
        </div>
      </div>
    </AccordionContent>
  );
};

// --- Main Event Item ---

export const S2SEventItem = ({
  event,
  configuration,
}: CombinedS2SEventAndConfiguration) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const { extraFields, getEventFieldDisplayName, getEventFieldIcon } =
    useEventListExtraFields();

  const isActive = !event.deactivationTime;

  const timeAgo = useTimeSince(event.activationTime);

  const severity = (event.severity ?? configuration?.severity ?? '')
    .toString()
    .toLowerCase();

  const title = [event.mmsiFirst, event.mmsiSecond].filter(Boolean).join(' ↔ ');

  const ackState = fromS2SAckState(
    (event as unknown as Record<string, unknown>).ack_state as string,
  );

  return (
    <AccordionListItem
      value={event.uuid as string}
      compact
      className={cn(
        'px-2 border-l-4 transition-all rounded-l-lg',
        isActive ? 'border-l-blue-500' : 'border-l-transparent',
      )}
      actionButtons={
        <div className="flex flex-col items-center gap-1">
          {ackState === 'unacknowledged' && (
            <Tooltip>
              <TooltipTrigger>
                <PiWarningCircle className="w-5 h-5 text-yellow-600" />
              </TooltipTrigger>
              <TooltipContent>Not acknowledged</TooltipContent>
            </Tooltip>
          )}
          {ackState === 'acknowledged' && (
            <Tooltip>
              <TooltipTrigger>
                <PiCheckCircle className="w-5 h-5 text-green-600" />
              </TooltipTrigger>
              <TooltipContent>
                <div>
                  <h3 className="font-medium text-base text-left">
                    Acknowledged
                  </h3>
                  <Separator className="my-2" />
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <p className="text-sm font-medium flex-none">By</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {
                          (event as unknown as Record<string, unknown>)
                            .ack_state_modified_by as string
                        }
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <p className="text-sm font-medium flex-none">At</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {formatDateTimeWithTimeZone(
                          new Date(
                            (event as unknown as Record<string, unknown>)
                              .ack_state_modified_ts as string,
                          ),
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      }
      triggerContent={
        <div className="space-y-1 flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <PiArrowsLeftRight className="w-4 h-4 flex-shrink-0" />
            <TruncatedText
              text={`${configuration?.name ?? 'Deleted detector'} - ${title || 'S2S Event'}`}
              className="text-xs text-left font-medium group-hover:underline"
              maxWidth="160px"
            />
          </div>
          <div className="flex items-center space-x-1.5">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                severity === 'note' && 'bg-blue-500',
                severity === 'warning' && 'bg-yellow-500',
                severity === 'alert' && 'bg-red-500',
              )}
            />
            <p className="text-sm text-left text-muted-foreground">{timeAgo}</p>
          </div>
          {extraFields.map((fieldKey) => {
            const IconComponent = getEventFieldIcon(fieldKey);
            return (
              <div
                key={fieldKey}
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconComponent className="w-3 h-3 flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {getEventFieldDisplayName(fieldKey)}
                  </TooltipContent>
                </Tooltip>
                <span className="text-left">
                  {getS2SEventFieldValue(
                    event,
                    configuration,
                    fieldKey,
                    formatDateTimeWithTimeZone,
                  )}
                </span>
              </div>
            );
          })}
        </div>
      }
      kebabMenu={
        <S2SEventDropDownMenu event={event} configuration={configuration} />
      }
    >
      <S2SEventItemContent event={event} configuration={configuration} />
    </AccordionListItem>
  );
};
